import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";

import { useCampaign, useQuestionnaire } from "./utils/CampaignStore";
import { AddSet, AnswerQuestion, DeleteAttachment, GetQuestionnaire, Question, RemoveSet, SubmitQuestionnaire } from "./utils/ServiceManagerAPI";

import { Link, useNavigate, useParams } from "react-router-dom";
import AnswerableQuestion from "./components/AnswerableQuestion";
import { useUser } from "./utils/AccountStore";

import SkeletonWrapper from "./components/SkeltonWrapper";
import { Anchor, Box, Button, Heading, Markdown, Text } from "grommet";

import StyledModal from "./components/StyledModel";
import LinkAnchor from "./components/LinkAnchor";
import AttachmentUpload from "./components/AttachmentUpload";

let Questions = () => {
    //@ts-ignore
    let { id } = useParams();

    let [loading, setLoading] = React.useState(true);
    let [submitting, setSubmitting] = React.useState(false);

    let { user } = useUser();
    let campaign = useCampaign();
    let questionnaire = useQuestionnaire(id);

    let navigate = useNavigate();

    const [questions, setQuestions] = React.useState([] as Question[]);
    //const [questionnaire, setQuestionnaire] = React.useState({} as { id: string });

    React.useEffect(() => {
        //@ts-ignore
        if (questionnaire.id) {
            setQuestions(questionnaire.questions);
            setLoading(false);
        }
    }, [questionnaire]);

    const refreshQuestionnaire = () => {
        return GetQuestionnaire(id).then((result) => {
            setQuestions(result.questionnaire.questions);
        });
    };

    let handleOptionChange = (value, option, questionId, setId?) => {
        setQuestions(
            questions.map((q) => {
                if (q.id == questionId) {
                    if (q.allowSets) {
                        q.sets?.forEach((s) => {
                            if (s.id == setId) {
                                s.options.forEach((o) => {
                                    if (o.id == option.id) {
                                        o.value = value;
                                    } else {
                                        if (o.type == 0 && !q.multipleOptions) {
                                            o.value = false;
                                        }
                                    }
                                });
                            }
                        });
                    } else {
                        q.options?.forEach((o) => {
                            if (o.id == option.id) {
                                o.value = value;
                            } else {
                                if (o.type == 0 && !q.multipleOptions) {
                                    o.value = false;
                                }
                            }
                        });
                    }
                }
                return q;
            })
        );

        return AnswerQuestion(questionnaire.id, questionId, setId, option).then(refreshQuestionnaire);
    };

    let addSet = (questionId) => {
        return AddSet(questionnaire.id, questionId).then((res: any) => {
            setQuestions(
                questions.map((q) => {
                    if (q.id == questionId) {
                        let options = q.options?.map((o) => {
                            let newOption = { ...o };
                            newOption.value = null;
                            return newOption;
                        });

                        res.options = options;
                        q.sets?.push(res);
                    }
                    return q;
                })
            );
            return res;
        });
    };

    let deleteSet = (questionId, setId) => {
        return RemoveSet(setId).then(() => {
            setQuestions(
                questions.map((q) => {
                    if (q.id == questionId) {
                        q.sets = q.sets?.filter((s) => s.id != setId);
                    }
                    return q;
                })
            );
        });
    };

    let attachmentUploaded = (attachment) => {
        refreshQuestionnaire();
        toggleAttachmentModal();
    };

    let deleteAttachment = (questionId, attachmentId) => {
        DeleteAttachment(attachmentId).then(() => {
            refreshQuestionnaire();
        });
    };

    let startSubmit = () => {
        setSubmitting(true);
    };

    let submit = () => {
        SubmitQuestionnaire(questionnaire.id).then(() => {
            setSubmitting(false);
            navigate(`/?tab=knowledge_check`);
        });
    };

    const [showAttachmentModal, setShowAttachmentModal] = React.useState(false);
    const [currentQuestionId, setCurrentQuestionId] = React.useState("");

    const toggleAttachmentModal = (questionId?) => {
        setShowAttachmentModal(!showAttachmentModal);
        setCurrentQuestionId(questionId);
    };

    return (
        <Box pad={"small"} margin={"small"} background={{ color: "background", opacity: 0.8 }} round="small">
            <LinkAnchor
                path="/"
                label={
                    <>
                        <FontAwesomeIcon icon={["fad", "chevron-left"]}></FontAwesomeIcon>
                        <Text size="large" style={{ marginLeft: "1rem" }}>
                            Home
                        </Text>
                    </>
                }
            ></LinkAnchor>

            <SkeletonWrapper loading={loading}>
                <div style={{ minHeight: "100px" }}>
                    {campaign && (
                        <Box pad={{ top: "small" }}>
                            <Markdown>{questionnaire?.questionnaireType?.description}</Markdown>
                            {questions.map((question, i) => (
                                <ConditionEvaluator key={i} question={question} questions={questions}>
                                    <AnswerableQuestion questionnaire={questionnaire} question={question} handleOptionChange={handleOptionChange} deleteSet={deleteSet} addSet={addSet} toggleAttachmentModal={toggleAttachmentModal} deleteAttachment={deleteAttachment} user={user} key={i}></AnswerableQuestion>
                                </ConditionEvaluator>
                            ))}
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                                <Button primary onClick={startSubmit} label="Submit"></Button>
                            </div>
                        </Box>
                    )}
                </div>
                <StyledModal isOpen={submitting} setIsOpen={setSubmitting}>
                    <Text>Are you ready to submit your answers? This submission is final and can't be changed</Text>
                    <Box direction="row" justify="between" pad="small">
                        <Button label="Cancel" onClick={() => setSubmitting(false)}></Button>
                        <Button primary label="Continue" onClick={submit}></Button>
                    </Box>
                </StyledModal>
                <StyledModal isOpen={showAttachmentModal} setIsOpen={setShowAttachmentModal}>
                    <Box width={"medium"}>
                        <Heading level="3">Add Attachment</Heading>
                        <AttachmentUpload questionId={currentQuestionId} questionnaireId={questionnaire.id} done={attachmentUploaded}></AttachmentUpload>
                        <Box direction="row" justify="between" pad="small">
                            <Button label="Close" onClick={() => setShowAttachmentModal(false)}></Button>
                        </Box>
                    </Box>
                </StyledModal>
            </SkeletonWrapper>
        </Box>
    );
};

function ConditionEvaluator({ question, questions, children }) {
    let show = false;

    if (question.conditions.length > 0) {
        question.conditions.forEach((condition) => {
            let reliesOn = questions.filter((q) => q.id == condition.reliesOnID)[0];

            if (reliesOn) {
                if (condition.type == 1 && reliesOn.answers && reliesOn.answers.length > 0) {
                    let result = false;

                    condition.conditionOptions.forEach((conditionOption) => {
                        reliesOn.options.forEach((option) => {
                            if (option.id == conditionOption.optionID && option.value) {
                                result = true;
                            }
                        });
                    });

                    show = show || result;
                }
            } else {
                show = false;
            }
        });
    } else {
        show = true;
    }

    return <>{show && children}</>;
}

export default Questions;
